// ---------------------------------------------------------------------------
// AG-012: Agency time-tracking entries
// ---------------------------------------------------------------------------
//
// Agency staff log time worked against client tenants (with optional task
// reference) for billing and reporting. An entry begins with `start()` (only
// `startedAt` set) and closes with `stop()` which writes `endedAt` and the
// computed `durationSeconds`. We persist `durationSeconds` as a stored column
// (computed from `endedAt - startedAt`) so list/summary queries do not have
// to recompute it on every read.
//
// Foreign keys:
//   agency_tenant_id  -> organization.id  (the agency doing the work)
//   client_tenant_id  -> organization.id  (the sub-account being worked on)
//   user_id           -> user.id          (who logged the time)
//   task_id           -> nullable text    (free-form task reference; not FK'd
//                                          because tasks live in many systems
//                                          — workflows, CRM, external tools —
//                                          and we don't want to couple here)
//
// All cross-agency access checks happen in the service layer
// (time-tracking-service.ts) using `agencyTenantId` as the authoritative
// scope. NEVER query by `clientTenantId` alone.

import {
  pgTable,
  uuid,
  text,
  integer,
  boolean,
  timestamp,
  index,
} from "drizzle-orm/pg-core";
import { sql } from "drizzle-orm";
import { organization, user } from "./auth";

const tz = { withTimezone: true } as const;

export const agencyTimeEntries = pgTable(
  "agency_time_entries",
  {
    id: uuid("id").primaryKey().defaultRandom(),
    agencyTenantId: text("agency_tenant_id")
      .notNull()
      .references(() => organization.id, { onDelete: "cascade" }),
    clientTenantId: text("client_tenant_id")
      .notNull()
      .references(() => organization.id, { onDelete: "cascade" }),
    userId: text("user_id")
      .notNull()
      .references(() => user.id, { onDelete: "restrict" }),
    taskId: text("task_id"),
    startedAt: timestamp("started_at", tz).notNull(),
    endedAt: timestamp("ended_at", tz),
    // Stored generated column — Postgres computes this on insert/update so
    // reads (list, summary) avoid recomputing per-row. NULL while the entry
    // is still running.
    durationSeconds: integer("duration_seconds").generatedAlwaysAs(
      sql`CASE WHEN ended_at IS NULL THEN NULL
               ELSE GREATEST(0, EXTRACT(EPOCH FROM (ended_at - started_at))::int)
          END`,
    ),
    notes: text("notes"),
    billable: boolean("billable").notNull().default(true),
    createdAt: timestamp("created_at", tz).notNull().defaultNow(),
  },
  (table) => [
    // Primary access pattern: list/summary scoped to one agency over a window.
    index("idx_agency_time_entries_agency_started").on(
      table.agencyTenantId,
      table.startedAt,
    ),
    // Filter by client within an agency (per-client invoicing).
    index("idx_agency_time_entries_agency_client").on(
      table.agencyTenantId,
      table.clientTenantId,
    ),
    // Filter by user within an agency (per-staff reporting).
    index("idx_agency_time_entries_agency_user").on(
      table.agencyTenantId,
      table.userId,
    ),
  ],
);

export type AgencyTimeEntry = typeof agencyTimeEntries.$inferSelect;
export type NewAgencyTimeEntry = typeof agencyTimeEntries.$inferInsert;
